import React, { useState } from "react";
import CardImg from "../../../components/CardImg";
import Collapse from "../../../components/Collapse";
import { formatBook, formatOdds, formatAmount } from "../../../utils/formatOdds";
import { Link } from "react-router-dom";
import { Modal } from "react-bootstrap";
import CasinoTableLayout from "../../casinoComponents/casinoTableLayout";
import { getCardImage } from "../../casinoComponents/resultVisuals/cardAssets";
import Teen20RefResults from "../resultui/resultTeen20/Teen20RefResults";
import { IS_REF_MOBILE } from "../../../utils/refViewport";
import { latestBets } from "../../../utils/casinoMyBets";
import { VTEEN20_RULES_HTML } from "./vteen20Rules";
import { Teen20RulesCard } from "./Teen20RightSide";
import imageSpade from "../../../assets/img/spade.png";
import imageClub from "../../../assets/img/club.png";
import imageHeart from "../../../assets/img/heart.png";
import imageDiamond from "../../../assets/img/diamond.png";

/**
 * Virtual 20-20 Teenpatti (vcasino/teen20, API type vteen20) in the reference markup:
 *   .casino-table.vcasino.vteen20 >
 *     [.progress.mobile-timer (ft > 3)]
 *     .casino-video > .casino-video-title + .video-box-container + .vcasino-cards + right icons (home, rules)
 *     .casino-detail > .main-odds (.odds-title + 6 × .odds-section) + .casino-video-last-results
 *   right sidebar: My Bets (UserName / Rate / Amount) + Rules card (Pair Plus payouts).
 */

const WINNER_LEFT = `${import.meta.env.BASE_URL}img/winner-left.png`;

function formatStake(num) {
  if (num == null) return "0";
  const n = Number(num);
  if (n >= 100000) return `${n / 100000}L`;
  if (n >= 1000) return `${n / 1000}K`;
  return String(n);
}

const isSuspendedItem = (item) =>
  item?.gstatus != null ? String(item.gstatus).toUpperCase() !== "OPEN" : !(Number(item?.b) > 0);

function formatBetDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

// Reference vcasino RightSideBar: My Bets card (3-column table) + Rules card
function VRightSide({ bets = [], onViewMore }) {
  const list = latestBets(bets); // latest 10, like the reference
  return (
    <>
      <div className="card m-b-10 my-bet" id={IS_REF_MOBILE ? "casino-bets" : undefined}>
        <div className="card-header">
          <h6 className="card-title float-left">My Bets</h6>{" "}
          <a
            href="javascript:void(0)"
            className="btn btn-back float-right"
            onClick={(e) => {
              e.preventDefault();
              if (typeof onViewMore === "function") onViewMore();
            }}
          >
            View More
          </a>
        </div>{" "}
        <div className="card-body1">
          <div className="tab-content">
            <div id="matched-bet" className="tab-pane active">
              <div className="table-responsive1">
                <table className="table coupon-table table-striped mb-0">
                  <thead>
                    <tr>
                      <th style={{ minWidth: "90px" }}>UserName</th>
                      <th className="text-right" style={{ minWidth: "50px" }}>Rate</th>
                      <th className="text-right" style={{ minWidth: "70px" }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center">No records found</td>
                      </tr>
                    ) : (
                      list.map((bet, i) => {
                        const t = String(bet.type || bet.gametype || "").toLowerCase();
                        const border = t === "lay" ? "lay-border" : t === "back" ? "back-border" : undefined;
                        return (
                          <React.Fragment key={bet.id || i}>
                            <tr className={border}>
                              <td colSpan={4}>
                                <b>{bet.selection || bet.matchedBet || "-"}</b>{" "}
                                <span className="float-right">{formatBetDate(bet.placeDate)}</span>
                              </td>
                            </tr>
                            <tr className={border}>
                              <td>{bet.userName}</td>
                              <td className="text-right">{formatOdds(bet.odds)}</td>
                              <td className="text-right">{formatAmount(bet.stake)}</td>
                            </tr>
                            <tr>
                              <td colSpan={4} style={{ height: "3px", padding: 0 }} />
                            </tr>
                          </React.Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>{" "}
      <Teen20RulesCard />
    </>
  );
}

export default function VTeen20View({ gameName, title, data, exposures = {}, onBetClick, myBets, onViewMore, viewMoreModal }) {
  const [openRange, setOpenRange] = useState(null);
  const [showRules, setShowRules] = useState(false);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const sub = Array.isArray(data?.sub) ? data.sub : [];
  const byNat = (nat) => sub.find((s) => s?.nat === nat) || { b: 0, min: 0, max: 0 };
  const cards = data?.card ? String(data.card).split(",").map((c) => c.trim()) : [];
  const ft = Number(data?.ft) || 0;
  const lt = Number(data?.lt) || 0;

  const cell = (nat, oddText) => {
    const item = byNat(nat);
    const suspended = isSuspendedItem(item);
    const exposure = exposures[nat] ?? 0;
    const text = formatBook(exposure);
    return (
      <div className="odds-box">
        <div className="casino-bl-box">
          <div
            className={`back casino-bl-box-item${suspended ? " suspended lock-top" : ""}`}
            onClick={() => !suspended && onBetClick?.(item?.b, nat, item)}
          >
            <span className="casino-box-odd">{oddText ?? item?.b ?? 0}</span>{" "}
            <span className={`casino-book ${exposure > 0 ? "book-green" : "book-red"}`}>{text}</span>
          </div>
        </div>
      </div>
    );
  };

  const range = (n, item) => {
    const id = `range${n}`;
    return (
      <div className="d-inline-block ml-2">
        <i
          role="button"
          tabIndex={0}
          data-toggle="collapse"
          data-target={`#${id}`}
          className="fas fa-info-circle"
          aria-expanded={openRange === id}
          onClick={() => setOpenRange((p) => (p === id ? null : id))}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setOpenRange((p) => (p === id ? null : id));
            }
          }}
        />{" "}
        <Collapse id={id} open={openRange === id} className="icon-range">
          {"\n                                            R:"}
          <span>{formatStake(item?.min)}</span>-<span>{formatStake(item?.max)}</span>
        </Collapse>
      </div>
    );
  };

  const section = (n, a, b, label, oddA, oddB) => (
    <div className="odds-section">
      {cell(a, oddA)}{" "}
      <div className="odds-box pt-2">
        {label} {range(n, byNat(a))}
      </div>{" "}
      {cell(b, oddB)}
    </div>
  );

  const card = (token, key) => (
    <span key={key}>
      <CardImg src={getCardImage(token)} alt="" />
    </span>
  );

  const rdesc = String(data?.rdesc ?? "");

  return (
    <CasinoTableLayout tableClass="vcasino vteen20" rightSide={<VRightSide bets={myBets} onViewMore={onViewMore} />}>
      <>
        {ft > 3 && (
          <div className="progress mobile-timer">
            <div
              className={`progress-bar ${lt <= 3 ? "bg-danger" : lt <= 6 ? "bg-warning" : "bg-success"}`}
              style={{ width: `${((100 * lt) / ft).toFixed(2)}%` }}
            />
          </div>
        )}{" "}
        <div className="casino-video">
          <div className="casino-video-title">
            <span className="casino-name">{title}</span>{" "}
            <div className="casino-video-rid">Round ID: {data?.mid}</div>
          </div>{" "}
          <div className="video-box-container">
            <div className="video-box" />
          </div>{" "}
          {cards.length > 0 && (
            <div className="vcasino-cards">
              <div className="player-card playera-card">
                {rdesc === "1" && <img src={WINNER_LEFT} className="v-winner" alt="" />}{" "}
                <div className="w-100 playera mb-2">Player A</div> {card(cards[0], 0)} {card(cards[2], 1)} {card(cards[4], 2)}
              </div>{" "}
              <div className="player-card playerb-card">
                {rdesc === "2" && <img src={WINNER_LEFT} className="v-winner" alt="" />}{" "}
                <div className="w-100 playerb mb-2">Player B</div> {card(cards[5], 0)} {card(cards[3], 1)} {card(cards[1], 2)}
              </div>
            </div>
          )}{" "}
          <div className="casino-video-right-icons">
            <div title="Home" className="casino-video-home-icon">
              <Link to="/admin/vcasino/list" style={{ color: "var(--text-highlight)" }}>
                <i className="fas fa-home" />
              </Link>
            </div>{" "}
            <div
              title="Rules"
              className="casino-video-rules-icon"
              onClick={() => {
                setShowRules(true);
                if (IS_REF_MOBILE) setShowRulesModal(true);
              }}
            >
              <i className="fas fa-info-circle" />
            </div>
          </div>{" "}
          <div>
            {showRules && (
              <div
                id="casino-vieo-rules"
                aria-modal="true"
                className="casino-vieo-rules show d-none-small show-rules"
                style={{ display: "block" }}
              >
                <div className="rules-header">
                  <div>Rules</div> <i className="fas fa-times" onClick={() => setShowRules(false)} />
                </div>{" "}
                <div className="rules-body" dangerouslySetInnerHTML={{ __html: VTEEN20_RULES_HTML }} />
              </div>
            )}
            <Modal
              show={showRulesModal}
              onHide={() => setShowRulesModal(false)}
              className="casino-rules teenpatti2-rule-modal"
              dialogClassName="modal-md"
              id="rules-modal"
            >
              <header className="modal-header">
                <h5 className="modal-title">Rules</h5>{" "}
                <button type="button" aria-label="Close" className="close" onClick={() => setShowRulesModal(false)}>
                  ×
                </button>
              </header>
              <Modal.Body>
                <div className="casino-vieo-rules show-rules show">
                  <div className="rules-body" dangerouslySetInnerHTML={{ __html: VTEEN20_RULES_HTML }} />
                </div>
              </Modal.Body>
            </Modal>
          </div>
        </div>{" "}
        {data?.mid ? (
          <div className="casino-detail">
            <div className="main-odds">
              <div className="odds-title">
                <div className="playera">Player A</div> <div>{" "}</div> <div className="playerb">Player B</div>
              </div>{" "}
              {section(1, "Player A", "Player B", <b>Main</b>)}{" "}
              {section(2, "3 Baccarat A", "3 Baccarat B", <b>3 Baccarat</b>)}{" "}
              {section(3, "Total A", "Total B", <b>Total</b>)}{" "}
              {section(4, "Pair Plus A", "Pair Plus B", <b>Pair Plus</b>, "A", "B")}{" "}
              {section(
                5,
                "Black A",
                "Black B",
                <>
                  <img src={imageSpade} alt="" /> <img src={imageClub} alt="" />
                </>
              )}{" "}
              {section(
                6,
                "Red A",
                "Red B",
                <>
                  <img src={imageHeart} alt="" /> <img src={imageDiamond} className="diamond-icon" alt="" />
                </>
              )}
            </div>{" "}
            <div className="casino-video-last-results">
              <Teen20RefResults gameType={gameName} fallbackTitle={title} />
            </div>
          </div>
        ) : null}
      </>
      {viewMoreModal}
    </CasinoTableLayout>
  );
}
